import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AiSettings from './ai/AiSettings';
import McpSetup from './mcp/McpSetup';
import { useToast } from './ui/Toast';

const Message = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center text-sm shrink-0 mr-2 mt-0.5">
          🤖
        </div>
      )}
      <div
        className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-purple-600/80 text-white rounded-tr-sm'
            : 'bg-slate-700/80 text-slate-200 rounded-tl-sm'
        }`}
      >
        {msg.content}
      </div>
    </div>
  );
};

/**
 * Call a local LLM (Ollama or LM Studio) directly from the browser.
 * The backend cannot reach localhost — the browser can because it runs
 * on the same machine as the local model.
 */
async function callLocalLlm({ provider, model, endpoint, systemPrompt, messages }) {
  if (provider === 'ollama') {
    const baseUrl = endpoint || 'http://localhost:11434';
    const res = await fetch(`${baseUrl}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: model || 'llama3',
        messages: [{ role: 'system', content: systemPrompt }, ...messages],
        stream: false,
      }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Ollama error ${res.status}: ${text}`);
    }
    const json = await res.json();
    return json.message?.content || '';
  }

  if (provider === 'lmstudio') {
    // LM Studio uses OpenAI-compatible API at /v1
    const baseUrl = (endpoint || 'http://localhost:1234') + '/v1';
    const res = await fetch(`${baseUrl}/chat/completions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: model || 'local-model',
        messages: [{ role: 'system', content: systemPrompt }, ...messages],
        max_tokens: 2048,
        temperature: 0.7,
        stream: false,
      }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`LM Studio error ${res.status}: ${text}`);
    }
    const json = await res.json();
    const msg = json.choices?.[0]?.message;
    // Thinking models (e.g. Gemma 4) put chain-of-thought in reasoning_content;
    // final answer in content. If content is empty, the model needs more tokens.
    return msg?.content || msg?.reasoning_content || '';
  }

  throw new Error(`Unknown local provider: ${provider}`);
}

const Strategy = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [mcpStatus, setMcpStatus] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchMcpStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/mcp/status');
      if (data.success) setMcpStatus(data.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchMcpStatus();
    setMessages([{
      role: 'assistant',
      content: `Welcome to your AI Strategy Session! I can help you analyze your trading patterns, plan your day, and validate setups. Ask me anything about your trading.${
        mcpStatus?.connected ? ' Your broker is connected — I can also see your live positions.' : ''
      }`,
    }]);
  }, [fetchMcpStatus, mcpStatus?.connected]);

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    setMessages(prev => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setSending(true);

    try {
      const { data } = await api.post('/mcp/query', { message: trimmed });

      if (data.needsLocalLlm) {
        // Backend can't reach localhost — browser calls the local model directly
        const response = await callLocalLlm({
          provider: data.provider,
          model: data.model,
          endpoint: data.endpoint,
          systemPrompt: data.systemPrompt,
          messages: data.messages,
        });

        const content = response || '(empty response — the model may need more tokens or the wrong model name)';
        setMessages(prev => [...prev, { role: 'assistant', content }]);

        // Persist the assistant turn in the backend session
        await api.post('/mcp/save-message', { assistantMessage: content }).catch(() => {});
        return;
      }

      if (data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.data.response }]);
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Failed to get AI response.';

      if (err.response?.status === 400 && errMsg.includes('LLM key')) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '⚠️ No AI provider configured. Click ⚙ Settings above to add your API key or connect a local model.',
        }]);
      } else if (errMsg.includes('fetch') || errMsg.includes('NetworkError') || errMsg.includes('Failed to fetch')) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '⚠️ Could not reach your local model. Make sure LM Studio / Ollama is running and the endpoint in Settings is correct.',
        }]);
      } else {
        toast.error(errMsg);
        setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${errMsg}` }]);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-200">Strategy Session</h1>
          <p className="text-xs text-slate-400">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            {mcpStatus?.connected && (
              <span className="ml-2 text-cyan-400">• Broker connected</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowSettings(s => !s)}
          className="text-xs px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700 text-slate-400 hover:text-slate-200 rounded-lg border border-slate-600/50 transition-colors"
        >
          ⚙ Settings
        </button>
      </div>

      {/* Collapsible Settings Panel */}
      {showSettings && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <AiSettings />
          <McpSetup />
        </div>
      )}

      {/* Chat area */}
      <div className="flex-1 overflow-y-auto bg-slate-800/30 rounded-xl border border-slate-700/50 p-4 mb-4">
        {messages.map((msg, i) => (
          <Message key={i} msg={msg} />
        ))}
        {sending && (
          <div className="flex justify-start mb-3">
            <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center text-sm shrink-0 mr-2 mt-0.5">
              🤖
            </div>
            <div className="bg-slate-700/80 text-slate-400 px-4 py-2.5 rounded-2xl rounded-tl-sm text-sm">
              <span className="animate-pulse">Thinking...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggested prompts */}
      <div className="flex gap-2 mb-3 flex-wrap">
        {[
          'What are my top edges today?',
          'What should I avoid trading?',
          'How is my performance this week?',
        ].map(suggestion => (
          <button
            key={suggestion}
            onClick={() => setInput(suggestion)}
            className="text-xs px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700 text-slate-400 hover:text-slate-200 rounded-lg transition-colors border border-slate-600/50"
          >
            {suggestion}
          </button>
        ))}
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Ask about your trades, setups, or market conditions..."
          disabled={sending}
          className="flex-1 bg-slate-700 border border-slate-600 text-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-500 placeholder-slate-500 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="px-4 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white rounded-xl text-sm font-medium transition-colors"
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default Strategy;
